import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { Alert } from '../../components/common/Alert';
import { 
  ExclamationTriangleIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

interface OverdueInvoice {
  id: number;
  invoice_number: string;
  client_name: string;
  client_email: string;
  client_phone?: string;
  amount_gross: number;
  currency: string;
  issue_date: string;
  due_date: string;
  reminder_count: number;
  last_reminder_sent?: string;
  days_overdue?: number; // This comes from backend
}

export const Overdue = () => {
  const [overdueInvoices, setOverdueInvoices] = useState<OverdueInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingReminder, setSendingReminder] = useState<number | null>(null);
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchOverdueInvoices();
  }, []);

  const fetchOverdueInvoices = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/invoices/overdue');
      setOverdueInvoices(response.data.invoices || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch overdue invoices');
      console.error('Error fetching overdue invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysOverdue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  const getDaysOverdueBadge = (days: number) => {
    if (days <= 7) return <Badge variant="warning">{days} dni</Badge>;
    if (days <= 30) return <Badge variant="error">{days} dni</Badge>;
    return <Badge variant="error">{days} dni</Badge>;
  };

  const handleSendReminder = async (invoiceId: number) => {
    try {
      setSendingReminder(invoiceId);
      await apiService.post(`/invoices/${invoiceId}/reminders`, {
        reminder_type: 'first'
      });
      
      showNotification({
        type: 'success',
        title: 'Sukces',
        message: 'Przypomnienie zostało wysłane!'
      });
    } catch (err: any) {
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: 'Błąd podczas wysyłania przypomnienia: ' + (err.response?.data?.message || err.message)
      });
    } finally {
      setSendingReminder(null);
    }
  };

  const handleCallClient = (phone: string) => {
    // Tutaj może być integracja z systemem telefonicznym
    window.open(`tel:${phone}`);
  };

  const totalOverdueAmount = overdueInvoices.reduce((sum, invoice) => sum + (Number(invoice.amount_gross) || 0), 0);
  const averageDaysOverdue = overdueInvoices.length > 0 ? 
    overdueInvoices.reduce((sum, inv) => sum + calculateDaysOverdue(inv.due_date), 0) / overdueInvoices.length : 0;
  const totalReminders = overdueInvoices.reduce((sum, inv) => sum + inv.reminder_count, 0);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Zaległości płatnicze</h1>
        <p className="mt-1 text-sm text-gray-500">
          Monitoruj i zarządzaj zaległymi fakturami
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Ładowanie zaległych faktur...</span>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchOverdueInvoices} variant="outline">
            Spróbuj ponownie
          </Button>
        </div>
      ) : (
        <>
          {/* Alert o zaległościach */}
          {overdueInvoices.length > 0 && (
            <Alert variant="warning" className="mb-6">
              <div>
                <p className="font-medium">Uwaga! Masz {overdueInvoices.length} zaległych faktur</p>
                <p className="text-sm">
                  Łączna wartość zaległości: {formatCurrency(totalOverdueAmount, 'PLN')}
                </p>
              </div>
            </Alert>
          )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Zaległe faktury
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {overdueInvoices.length}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Łączna wartość
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatCurrency(totalOverdueAmount, 'PLN')}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Średnie opóźnienie
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {Math.round(averageDaysOverdue)} dni
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <EnvelopeIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Wysłane przypomnienia
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {totalReminders}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overdue Invoices Table */}
      <Card>
        <CardHeader>
          <CardTitle>Zaległe faktury</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Faktura
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Klient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kwota
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Termin płatności
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opóźnienie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ostatnie przypomnienie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {overdueInvoices.map((invoice) => {
                  const daysOverdue = calculateDaysOverdue(invoice.due_date);
                  return (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invoice.invoice_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{invoice.client_name}</div>
                          <div className="text-sm text-gray-500">{invoice.client_email}</div>
                          {invoice.client_phone && (
                            <div className="text-sm text-gray-500">{invoice.client_phone}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(invoice.amount_gross, invoice.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(invoice.due_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getDaysOverdueBadge(invoice.days_overdue || daysOverdue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>
                          <div>{formatDate(invoice.last_reminder_sent || null)}</div>
                          <div className="text-xs text-gray-400">
                            ({invoice.reminder_count} przypomnienia)
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendReminder(invoice.id)}
                            disabled={sendingReminder === invoice.id}
                          >
                            {sendingReminder === invoice.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-1"></div>
                                Wysyłanie...
                              </>
                            ) : (
                              <>
                                <EnvelopeIcon className="h-4 w-4 mr-1" />
                                Wyślij przypomnienie
                              </>
                            )}
                          </Button>
                          {invoice.client_phone && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCallClient(invoice.client_phone!)}
                            >
                              <PhoneIcon className="h-4 w-4 mr-1" />
                              Zadzwoń
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
};